import { ClockCircleOutlined } from "@ant-design/icons";
import { Button, InputNumber, notification, Space, Statistic } from "antd";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { AuctionResponse, BidderAcceptBidStatus, BidderDetail, StartStatus, WebsocketTypes } from "../../../types";
import { ReactComponent as Gavel } from '../../../../../assets/images/gavel.svg';
import { ReactComponent as Payment } from '../../../../../assets/images/pay_icon_white.svg';
import { ReactComponent as Award } from '../../../../../assets/images/award.svg';
import { ReactComponent as Cup } from '../../../../../assets/images/cup.svg';
import { ReactComponent as Withdraw } from '../../../../../assets/images/withdraw.svg';
import { ReactComponent as Failed } from '../../../../../assets/images/failed.svg';
import { AuctionMethodCode, AuctionTypeCode, preCostTypes, registerTypes, SocketMessage, User } from "../../../../common/types/types";
import { useAppDispatch, useAppSelector } from "../../../../../redux/Hooks";
import ConfirmModal from "../../../../common/components/ConfirmModal";
import { useAcceptBidderMutation, useBidBargainProductMutation, useLazyCheckBargainProductQuery, useLazyGroupBargainByLevelQuery, useRefuseBargainMutation, useRefuseWithdrawMutation, useWithdrawBargainProductMutation } from "../../../redux/AuctionApi";
import { SpecsResponse } from "../../../../../redux/api/apiTypes";
import { useParams } from "react-router-dom";
import { addMinutes, differenceInMilliseconds, differenceInSeconds, intervalToDuration, isBefore, isEqual } from "date-fns";
import { setCheckWinTrigger, setDeclinedWin, setStopCountdown, setUpAuctionFinish, toggleAcceptOpen } from "../../../redux/AuctionSlice";
import AuctionAcceptConfirmModal from "./AuctionAcceptConfirmModal";

interface Countdown {
    day: number,
    hour: number,
    minute: number,
    second: number
}

interface AuctionPriceStatusProps {
    product?: AuctionResponse,
    countdown: Countdown,
    startingPrice: number,
    currentBid?: BidderDetail,
    setStartingPrice: React.Dispatch<React.SetStateAction<number>>,
    setSecondUserVisible: React.Dispatch<React.SetStateAction<boolean>>,
    setFailVisible: React.Dispatch<React.SetStateAction<boolean>>,
    onStartingPriceChange: () => void,
    socketMessage?: SocketMessage,
    specsList: SpecsResponse[],
    triggerFetch: () => void,
    bargainIndex: [{ user?: User }] | [],
    secondUserVisible: boolean,
    toggleSecondUserVisible: () => void,
    hasShownModals: boolean;
    setHasShownModals: React.Dispatch<React.SetStateAction<boolean>>,
    checkFinish: boolean,
    setCheckFinish: React.Dispatch<React.SetStateAction<boolean>>,
}

const AuctionPriceStatus = ({ product, countdown, startingPrice, currentBid,
    setStartingPrice, onStartingPriceChange, socketMessage, specsList, triggerFetch, setSecondUserVisible,
    setFailVisible, bargainIndex, secondUserVisible, toggleSecondUserVisible, hasShownModals, setHasShownModals, checkFinish, setCheckFinish }: AuctionPriceStatusProps) => {
    const [acceptCountdown, setAcceptCountdown] = useState<Countdown>({ day: 0, hour: 0, minute: 0, second: 0 });
    const [countdownEnd, setCountdownEnd] = useState(0);
    const [cardClassName, setCardClassName] = useState("");
    const [inputFocus, setInputFocus] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);
    const [value, setValue] = useState<string>("0");
    const [bidVisible, setBidVisible] = useState(false);
    const [withdrawVisible, setWithdrawVisible] = useState(false);
    const [successVisible, setSuccessVisible] = useState(false);
    const [refuseVisible, setRefuseVisible] = useState(false);
    const [refuseSuccessVisible, setRefuseSuccessVisible] = useState(false);
    const [waitForTurnVisible, setWaitForTurnVisible] = useState(false);
    const withdrawTimer = useRef<ReturnType<typeof setInterval> | null>();

    const userState = useAppSelector(state => state.login);
    const auctionState = useAppSelector(state => state.auction);
    const params = useParams();
    const dispatch = useAppDispatch();
    const accepted = useRef(false);

    const [submitBid, { isLoading }] = useBidBargainProductMutation();
    const [acceptBidder] = useAcceptBidderMutation();
    const [widthdrawBargain] = useWithdrawBargainProductMutation();
    const [checkBargainProduct] = useLazyCheckBargainProductQuery();
    const [groupTrigger] = useLazyGroupBargainByLevelQuery();
    const [refuseBargain] = useRefuseBargainMutation();
    const [refuseWithdraw] = useRefuseWithdrawMutation();

    useEffect(() => {
        if ((successVisible || refuseSuccessVisible) && userState.user.userId && product?.productId && product?.statute.statuteId) {
            localStorage.setItem(`${userState.user.userId}-${product?.productId}-${product?.statute.statuteId}`, JSON.stringify(true))
            setHasShownModals(true);
        }
    }, [product?.productId, product?.statute.statuteId, setHasShownModals, successVisible, userState.user.userId, refuseSuccessVisible])

    useEffect(() => {
        if (socketMessage?.type === WebsocketTypes.XAC_NHAN_GIA_TRUNG && socketMessage.userId !== userState.user.userId) {
            // Ẩn tất cả popup khi có người khác ấn xác nhận
            setWaitForTurnVisible(false);
            setBidVisible(false);
            // setWithdrawVisible(false);
            // setSuccessVisible(false);
            // setRefuseSuccessVisible(false);
            setRefuseVisible(false);
            setSecondUserVisible(false);
        }
    }, [socketMessage?.type, socketMessage?.userId, userState.user.userId, setSecondUserVisible, socketMessage])

    useEffect(() => {
        // Cập nhật giá hiện tại sau mỗi vòng
        if (product?.productId && auctionState.level && product?.biddingCostPerBid && product?.statute?.auctionMethod?.code === AuctionMethodCode.INCREASE) {
            let baseArray = auctionState.level - 1 > 0 ? Array(auctionState.level - 1) : Array(1);
            let levels = Array.from(baseArray, (x, i) => (i + 1));
            groupTrigger({ productId: product?.productId, lstLevel: levels, page: 0, size: 1 }).unwrap().then(res => {
                // Lấy giá lớn nhất hiện tại, nếu không có thì lấy giá khởi điểm
                let currentValue = res.content[0]?.valueOfBid;
                if (!res.content.length) {
                    currentValue = product?.startPrice || 0;
                } else {
                    if ((!res.content[0]?.level || res.content[0].level === auctionState.level || auctionState.level === 1) && !auctionState.upAuctionFinish) {
                        currentValue = product?.startPrice || 0;
                    } else if (auctionState.upAuctionFinish) {
                        currentValue = product?.bidderAcceptBid?.valueOfBid || 0;
                    }
                }

                if (!auctionState.upAuctionFinish) {
                    // Cộng thêm bước giá
                    currentValue += product?.biddingCostPerBid;
                }
                setStartingPrice(currentValue);
                setValue(currentValue.toString());
            })
        }
    }, [groupTrigger, product?.productId, setStartingPrice, product?.startPrice, auctionState.level, 
        product?.biddingCostPerBid, auctionState.upAuctionFinish, product?.bidderAcceptBid?.valueOfBid, product?.statute?.auctionMethod?.code])

    useEffect(() => {
        // Tạm dừng, tiếp tục countdown khi nhận được websocket
        if (product?.statute?.auctionMethod?.code === AuctionMethodCode.DECREASE) {
            if (product?.statute.allowWithdrawPaid && product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE) {
                dispatch(setStopCountdown(true));
                if (socketMessage?.userId !== userState.user.userId && product?.bidderAcceptBid?.user?.userId !== userState.user.userId
                    && product?.statute.allowWithdrawPaid && product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE) {
                    setWaitForTurnVisible(true);
                }
            } else if (socketMessage?.type === WebsocketTypes.RUT_LAI_GIA) {
                dispatch(setStopCountdown(false));
                setWaitForTurnVisible(false);
            }
        }
    }, [dispatch, product?.bidderAcceptBid?.status, product?.bidderAcceptBid?.user?.userId,
        product?.statute.allowWithdrawPaid, product?.statute?.auctionMethod?.code,
        socketMessage?.type, socketMessage?.userId, userState.user.userId, socketMessage])

    useEffect(() => {
        // Hiển thị modal xác nhận trúng giải khi người dùng vào sau khi thời gian kết thúc
        if (product?.statute?.auctionMethod?.code === AuctionMethodCode.INCREASE && auctionState.checkWinTriggered) {
            // Trả giá lên
            let endAuctionDate = 0;
            if (product?.startAuctionDate && product?.statute?.totalRound && product?.statute?.incrementInTimePerBid) {
                // Tính thời gian kết thúc trong trường hợp xác định vòng
                endAuctionDate = addMinutes(product.startAuctionDate, product.statute.totalRound * product.statute.incrementInTimePerBid).getTime();
            } else if (product?.statute?.timePerProduct) {
                // Tính thời gian kết thúc trong trường hợp không xác định vòng
                endAuctionDate = addMinutes(product.startAuctionDate, product.statute.timePerProduct).getTime();
            }
            let timeDiff = differenceInSeconds(new Date().getTime(), endAuctionDate);
            if (timeDiff >= 0 && product?.startStatus === StartStatus.ONGOING) {
                // Khi đã hết thời gian đấu giá
                dispatch(setUpAuctionFinish(true));
                dispatch(setCheckWinTrigger(true));
                let timeToUserOneConfirm = specsList.find(item => item.configKey === "timeToUserOneConfirm")?.configValue || 1;
                let timeToUserTwoConfirm = specsList.find(item => item.configKey === "timeToUserTwoConfirm")?.configValue || 1;
                let isCurrentWinner = (product?.bidderAcceptBid?.user?.userId === userState.user.userId &&
                    product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE);

                // if (!isCurrentWinner) {
                //     getBargainByIndex({ productId: product?.productId, index: 0 })
                // }

                let countdownEnd = endAuctionDate + timeToUserOneConfirm * 60 * 1000;
                if (product?.timeRefusebargainFirt) {
                    countdownEnd = product.timeRefusebargainFirt + timeToUserTwoConfirm * 60 * 1000;
                }

                if (withdrawTimer.current) {
                    clearInterval(withdrawTimer.current);
                    withdrawTimer.current = null;
                }
                setCountdownEnd(countdownEnd);

                if (timeDiff < timeToUserOneConfirm * 60 * 1000 && !product?.timeRefusebargainFirt && isCurrentWinner && !auctionState.declinedWin) {
                    // Người một xác nhận
                    dispatch(toggleAcceptOpen(true));
                } else if (product?.timeRefusebargainFirt && isCurrentWinner && !auctionState.declinedWin) {
                    // Người hai xác nhận
                    let userTwoTimeDiff = differenceInMilliseconds(new Date(), product.timeRefusebargainFirt);
                    if (userTwoTimeDiff < timeToUserTwoConfirm * 60 * 1000) {
                        setSecondUserVisible(false);
                        dispatch(toggleAcceptOpen(true));
                    }
                } else if (!isCurrentWinner) {
                    dispatch(toggleAcceptOpen(false));
                }
            }

            // Chỉ kiểm tra xác nhận trúng đấu giá khi load trang lần đầu
            // checkedForWin.current = true;
        }
    }, [auctionState.acceptOpen, auctionState.declinedWin, dispatch,
    product?.bidderAcceptBid?.status, product?.bidderAcceptBid?.user?.userId, product?.startAuctionDate,
    product?.startStatus, product?.statute?.auctionMethod?.code, product?.statute.incrementInTimePerBid,
    product?.statute.timePerProduct, product?.statute.totalRound, product?.timeRefusebargainFirt,
        setSecondUserVisible, specsList, userState.user.userId, auctionState.checkWinTriggered])

    useEffect(() => {
        // Chấp nhận đấu giá khi thời gian đếm ngược đã hết
        if (countdownEnd && product?.startStatus === StartStatus.ONGOING) {
            const newTimer = setInterval(() => {
                if (withdrawTimer.current && newTimer !== withdrawTimer.current) {
                    clearInterval(withdrawTimer.current);
                }

                withdrawTimer.current = newTimer;

                let currentDate = new Date();
                if (isBefore(countdownEnd, currentDate) || isEqual(countdownEnd, currentDate)) {
                    // Hết thời gian đếm ngược
                    setAcceptCountdown({
                        day: 0,
                        hour: 0,
                        minute: 0,
                        second: 0,
                    })

                    // Tự động xác nhận
                    let autoAcceptIncrease = product?.statute?.auctionMethod?.code === AuctionMethodCode.INCREASE && product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE;
                    let autoAcceptDecrease = product?.statute?.auctionMethod?.code === AuctionMethodCode.DECREASE && ((!product?.statute?.allowWithdrawPaid &&
                        product?.bidderAcceptBid?.status === BidderAcceptBidStatus.ACCEPT));

                    if (params.id && parseInt(params.id) &&
                        product?.bidderAcceptBid?.user?.userId === userState.user.userId && !accepted.current
                        && product?.startStatus === StartStatus.ONGOING) {
                        if (autoAcceptDecrease || autoAcceptIncrease) {
                            // Tự động xác nhận trúng
                            acceptBidder({
                                productId: parseInt(params.id),
                            }).unwrap().then(() => {
                                dispatch(toggleAcceptOpen(false));
                                if (!hasShownModals && checkFinish) {
                                    setSuccessVisible(true);
                                }
                                setCountdownEnd(0);
                                notification.success({
                                    message: "Xác nhận trúng thành công!"
                                });
                                accepted.current = true;
                            }).catch(err => {
                                notification.error({
                                    message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                                })
                                dispatch(toggleAcceptOpen(false));
                                accepted.current = true;
                            })
                        } else if (product?.statute?.auctionMethod?.code === AuctionMethodCode.DECREASE &&
                            product?.statute.allowWithdrawPaid && product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE
                            && product?.startStatus === StartStatus.ONGOING) {
                            // Tự động từ chối rút lại giá khi đặt giá xuống
                            refuseWithdraw(parseInt(params.id)).unwrap().then(() => {
                                dispatch(toggleAcceptOpen(true));
                            }).catch(err => {
                                notification.error({
                                    message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                                })
                                dispatch(toggleAcceptOpen(false));
                                // dispatch(setStopCountdown(false));
                            })
                        }

                        // Cập nhật lại chi tiết
                        triggerFetch();
                    }

                    if (withdrawTimer.current) {
                        clearInterval(withdrawTimer.current);
                        withdrawTimer.current = null;
                    }
                } else if (!isBefore(countdownEnd, currentDate) && !isEqual(countdownEnd, currentDate)) {
                    // Cập nhật thời gian đếm ngược
                    let difference = intervalToDuration({ start: new Date(), end: countdownEnd });
                    setAcceptCountdown({
                        day: difference.days || 0,
                        hour: difference.hours || 0,
                        minute: difference.minutes || 0,
                        second: difference.seconds || 0,
                    })
                }
            }, 1000);
        }

        if (product?.startStatus === StartStatus.FINISH && withdrawTimer.current) {
            clearInterval(withdrawTimer.current);
            withdrawTimer.current = null;
        }
    }, [countdownEnd, acceptBidder, dispatch, params.id, product?.bidderAcceptBid, userState.user.userId, hasShownModals, checkFinish, triggerFetch,
        product?.statute, refuseWithdraw, specsList, product?.startStatus])

    useEffect(() => {
        // Hiển thị trạng thái đấu giá
        switch (product?.startStatus) {
            case StartStatus.UPCOMING:
                setCardClassName("yellow-card");
                break;
            case StartStatus.ONGOING:
                setCardClassName("green-card");
                break;
            case StartStatus.PAUSE:
                setCardClassName("grey-card");
                break;
            case StartStatus.FINISH:
                if (!product?.bidderAcceptBid || product?.bidderAcceptBid?.status !== BidderAcceptBidStatus.SUCCESS) {
                    setCardClassName("grey-card");
                } else {
                    setCardClassName("green-card");
                }
                break;
            default:
                setCardClassName("grey-card");
                break;
        }
    }, [product?.bidderAcceptBid, product?.startStatus])

    useEffect(() => {
        if (product?.statute?.auctionType?.code !== AuctionTypeCode.HAVE_ROUND) {
            // Lấy giá hiện tại
            let valueOfBid = currentBid?.valueOfBid || product?.bidderAcceptBid?.valueOfBid || product?.startPrice || 0;
            if (product?.statute?.auctionMethod?.code === AuctionMethodCode.INCREASE && product?.biddingCostPerBid) {
                valueOfBid += product?.biddingCostPerBid;
            }
            setStartingPrice(valueOfBid);
            setValue(valueOfBid.toString());
        }
    }, [currentBid, product?.startPrice, setStartingPrice, product?.statute?.auctionType?.code,
        product?.bidderAcceptBid?.valueOfBid, product?.biddingCostPerBid, product?.statute?.auctionMethod?.code])

    useEffect(() => {
        if (product?.statute?.auctionMethod?.code === AuctionMethodCode.DECREASE &&
            (product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE || product?.bidderAcceptBid?.status === BidderAcceptBidStatus.ACCEPT)) {
            // Khi có người dùng ấn trả giá trong trường hợp đặt giá xuống
            // Dừng đếm ngược thời gian đấu giá
            dispatch(setStopCountdown(true));

            const hasAccepted = !product?.statute.allowWithdrawPaid || product?.bidderAcceptBid?.status === BidderAcceptBidStatus.ACCEPT;
            let timeAllowedToWithdrawPrice = specsList.find(item => item.configKey === "timeAllowedToWithdrawPrice")?.configValue || 1;
            let timeToUserOneConfirm = specsList.find(item => item.configKey === "timeToUserOneConfirm")?.configValue || 1;

            let timeLimit = timeAllowedToWithdrawPrice;
            if (hasAccepted) {
                timeLimit = timeToUserOneConfirm;
            }
            let startCountdownDate = product?.bidderAcceptBid?.modifiedDate || 0;
            // Đặt thời gian hết countdown rút lại giá
            // Thời gian hết countdown = thời gian ấn trả giá + thời gian để rút lại giá
            let toDate = addMinutes(startCountdownDate, timeLimit).getTime();
            if (withdrawTimer.current) {
                clearInterval(withdrawTimer.current);
                withdrawTimer.current = null;
            }
            setCountdownEnd(toDate);

            let timeDiff = differenceInSeconds(new Date().getTime(), startCountdownDate);
            if (hasAccepted && timeDiff < timeToUserOneConfirm * 60 * 1000 && product?.bidderAcceptBid.user?.userId === userState.user.userId) {
                dispatch(toggleAcceptOpen(true));
            }
        } else if (!product?.bidderAcceptBid) {
            // Chưa có ai chấp nhận giá thì tiếp tục đếm ngược
            dispatch(setStopCountdown(false));
        }

        return () => {
            if (withdrawTimer.current) {
                clearInterval(withdrawTimer.current);
                withdrawTimer.current = null;
            }
        };
    }, [dispatch, product?.bidderAcceptBid, product?.statute.allowWithdrawPaid, product?.statute?.auctionMethod?.code, specsList, userState.user.userId])


    const onInputChange = (price: string) => {
        let newValue = Number(price) < Number(value) ? value : price;
        setValue(newValue);
    }

    const onInputFocus = () => {
        setInputFocus(true);
        const bodyScrollTop = document.documentElement.scrollTop;
        setScrollTop(bodyScrollTop);
        document.body.style.top = `-${bodyScrollTop}px`;
        document.body.classList.add("scroll-disabled");
    }

    const onInputBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
        let newValue = Number(event.target.value.replace(/\./g, ""));
        setValue(newValue < Number(startingPrice) ? startingPrice.toString() : newValue.toString());
        setInputFocus(false);
        document.body.classList.remove("scroll-disabled");
        document.documentElement.scrollTop = scrollTop;
        document.body.style.removeProperty("top");
    }

    const onInputScroll = (event: React.WheelEvent<HTMLInputElement>) => {
        if (inputFocus) {
            let currentValue = Number(value);
            if (event.deltaY < 0) {
                currentValue += product?.biddingCostPerBid || 0;
            } else {
                currentValue -= product?.biddingCostPerBid || 0;
                currentValue = currentValue < startingPrice ? startingPrice : currentValue;
            }
            setValue(currentValue.toString());
        }
    }

    const onPriceSubmit = () => {
        if (params.id && parseInt(params.id)) {
            let productId = parseInt(params.id);
            submitBid({
                ...auctionState.level && product?.statute?.auctionType?.code === AuctionTypeCode.HAVE_ROUND ? { level: auctionState.level } : {},
                productId: productId,
                valueOfBid: parseInt(value),
            }).unwrap().then(() => {
                onStartingPriceChange();
                toggleBidVisible();

                if (product?.statute?.auctionMethod?.code === AuctionMethodCode.DECREASE && product?.statute?.allowWithdrawPaid) {
                    notification.open({
                        message: `Bạn có ${specsList.find(item => item.configKey === "timeAllowedToWithdrawPrice")?.configValue || 1} phút để rút lại giá hoặc xác nhận trúng đấu giá. Nếu rút lại giá bạn sẽ bị truất quyền tham gia đấu giá.\n` +
                            "Hết thời gian xác nhận, hệ thống sẽ tự động ghi nhận bạn là người trúng đấu giá."
                    })
                } else {
                    notification.success({
                        message: "Trả giá thành công!"
                    })
                }
            }).catch(err => {
                notification.error({
                    message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                })
                toggleBidVisible();
            })
        }
    }

    const toggleBidVisible = () => {
        setBidVisible(!bidVisible);
    }

    const toggleWithdrawVisible = () => {
        setWithdrawVisible(!withdrawVisible);
    }

    const toggleSuccessVisible = () => {
        if (successVisible || (!hasShownModals && checkFinish)) {
            setSuccessVisible(!successVisible);
        }
    }

    const toggleRefuseVisible = () => {
        setRefuseVisible(!refuseVisible);
    }

    const toggleRefuseSuccessVisible = () => {
        setRefuseSuccessVisible(!refuseSuccessVisible);
    }

    const buttonDisabled = () => {
        if (product && startingPrice >= product.startPrice) {
            // return Number(value) - Number(startingPrice) < product.biddingCostPerBid
            return Number(value) < Number(startingPrice)
        }
        return false;
    }

    const bidDownAcceptPriceClick = () => {
        if (startingPrice) {
            setValue(startingPrice.toString());
            toggleBidVisible();
        }
    }

    const onWidthdrawClick = () => {
        if (params.id && parseInt(params.id)) {
            widthdrawBargain({
                productId: parseInt(params.id),
            }).unwrap().then(() => {
                setCountdownEnd(0);
                toggleWithdrawVisible();
                triggerFetch();
                notification.success({
                    message: "Rút lại giá thành công!"
                });
                dispatch(setStopCountdown(false));
            }).catch(err => {
                notification.error({
                    message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                })
                dispatch(toggleAcceptOpen(false));
                // dispatch(setStopCountdown(false));
            })
        }
    }

    const onAcceptClick = () => {
        if (params.id && parseInt(params.id) && !accepted.current) {
            acceptBidder({
                productId: parseInt(params.id),
            }).unwrap().then(() => {
                if (withdrawTimer.current) {
                    clearInterval(withdrawTimer.current);
                    withdrawTimer.current = null;
                }
                setCountdownEnd(0);
                dispatch(toggleAcceptOpen(false));
                toggleSuccessVisible();
                notification.success({
                    message: "Xác nhận trúng thành công!"
                });
                dispatch(setStopCountdown(false));
                accepted.current = true;
                setHasShownModals(true);
            }).catch(err => {
                notification.error({
                    message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                })
                dispatch(toggleAcceptOpen(false));
                dispatch(setStopCountdown(false));
            })
        }
    }

    const onDenyClick = () => {
        if (product?.productId) {
            refuseBargain(product.productId).unwrap().then(() => {
                if (withdrawTimer.current) {
                    clearInterval(withdrawTimer.current);
                    withdrawTimer.current = null;
                }
                dispatch(toggleAcceptOpen(false));
                dispatch(setDeclinedWin(true));
                setCountdownEnd(0);
                toggleRefuseVisible();
                toggleRefuseSuccessVisible();
                setHasShownModals(true);
            }).catch(err => {
                notification.error({
                    message: err.data?.message || "Từ chối xác nhận trúng thất bại!"
                })
                toggleRefuseVisible();
            })
        }
    }

    const onSuccessClick = () => {
        localStorage.setItem(`${userState.user.userId}-${product?.productId}-${product?.statute.statuteId}`, JSON.stringify(true))
        toggleSuccessVisible();
        triggerFetch();
    }

    const onPriceSubmitClick = async () => {
        if (product?.statute.auctionType?.code === AuctionTypeCode.HAVE_ROUND &&
            userState?.user?.userId && params?.id && parseInt(params.id)) {
            // Kiểm tra người dùng đã trả giá trong vòng hiện tại hay chưa
            checkBargainProduct({
                levels: [auctionState.level],
                productId: parseInt(params.id),
                userId: userState.user.userId,
            }).unwrap().then((res) => {
                if (!res?.length) {
                    onPriceSubmit();
                } else {
                    notification.error({ message: "Bạn đã trả giá trong vòng hiện tại!" })
                    toggleBidVisible();
                }
            }).catch(err => {
                notification.error({
                    message: err.data?.message || "Không thể trả giá trong vòng hiện tại!"
                })
                toggleBidVisible();
            })
        } else if (product?.statute?.auctionMethod?.code === AuctionMethodCode.INCREASE &&
            product?.statute?.auctionType?.code === AuctionTypeCode.NOT_ROUND &&
            !product?.statute?.bidRule &&
            product?.bidderAcceptBid?.user?.userId === userState.user.userId) {
            // Kiểm tra trong trường hợp đấu giá liên tục
            notification.error({
                message: "Vui lòng đợi người tiếp theo trả giá!"
            })
            toggleBidVisible();
        } else {
            onPriceSubmit();
        }
    }

    const generateCountdownText = () => {
        let countdownText: ReactNode[] = [];
        if (acceptCountdown.day) {
            countdownText.push((
                <React.Fragment key="day">
                    <span style={{ fontWeight: 700 }}>{`${acceptCountdown.day} `}</span>
                    <span>ngày </span>
                </React.Fragment>
            ))
        }
        if (acceptCountdown.hour) {
            countdownText.push((
                <React.Fragment key="hour">
                    <span style={{ fontWeight: 700 }}>{`${acceptCountdown.hour} `}</span>
                    <span>giờ </span>
                </React.Fragment>
            ))
        }
        if (acceptCountdown.minute) {
            countdownText.push((
                <React.Fragment key="minute">
                    <span style={{ fontWeight: 700 }}>{`${acceptCountdown.minute} `}</span>
                    <span>phút </span>
                </React.Fragment>
            ))
        }

        countdownText.push((
            <React.Fragment key="second">
                <span style={{ fontWeight: 700 }}>{`${acceptCountdown.second || 0} `}</span>
                <span>giây</span>
            </React.Fragment>
        ))

        return (<>{countdownText.map(item => item)}</>)
    }

    const onBidConfirmClick = () => {
        if (params.id && parseInt(params.id)) {
            refuseWithdraw(parseInt(params.id)).unwrap().then(() => {
                dispatch(toggleAcceptOpen(true))
            }).catch(err => {
                notification.error({
                    message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                })
                dispatch(toggleAcceptOpen(false));
                // dispatch(setStopCountdown(false));
            })
        }
    }


    return (
        <div className={cardClassName}>

            {/* Hiển thị khi sắp diễn ra đấu giá */}
            {(!product?.startStatus || product?.startStatus === StartStatus.UPCOMING) ?
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <ClockCircleOutlined style={{ fontSize: '18px' }} />
                        &nbsp;<span> THỜI GIAN BẮT ĐẦU ĐẤU GIÁ CÒN</span>
                    </div>
                    <div className="item-card-action-list yellow-action-list">
                        <Space size={5}>
                            <div className="item-card-time">{countdown.day}</div>
                            <div className="item-card-time">{countdown.hour}</div>
                            <div className="item-card-time">{countdown.minute}</div>
                            <div className="item-card-time">{countdown.second}</div>
                        </Space>
                    </div>
                </div> : <></>
            }

            {/* Hiển thị khi đang diễn ra đấu giá */}
            {product?.startStatus === StartStatus.ONGOING ?
                <>
                    {/* Hiển thị thời gian đấu giá */}
                    {!auctionState.upAuctionFinish ?
                        <>
                            {/* Chưa hết thời gian trả giá tài sản */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Gavel style={{ fontSize: '18px' }} />
                                    &nbsp;<span style={{ color: 'white', marginLeft: 5 }}>
                                        {` ĐANG DIỄN RA ĐẤU GIÁ ${auctionState.level && !auctionState.upAuctionFinish ? `VÒNG ${auctionState.level}` : ''}`}
                                    </span>
                                </div>
                                <div className="item-card-action-list">
                                    <Space size={5}>
                                        <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{countdown.day}</div>
                                        <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{countdown.hour}</div>
                                        <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{countdown.minute}</div>
                                        <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{countdown.second}</div>
                                    </Space>
                                </div>
                            </div>
                        </> :
                        <>
                            {product?.bidderAcceptBid && product?.bidderAcceptBid.user ?
                                <>
                                    {/* Đã hết thời gian trả giá sản phẩm, chờ xác nhận trúng đấu giá */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Gavel style={{ fontSize: '18px' }} />
                                            &nbsp;<span style={{ color: 'white', marginLeft: 5 }}>
                                                {/* {`ĐANG XÁC NHẬN ${!bargainIndex.length || (product.bidderAcceptBid?.user?.userId &&
                                            bargainIndex?.[0]?.user?.userId !== product.bidderAcceptBid?.user.userId &&
                                            !product.bidderResponseDTO?.isActive &&
                                            !auctionState.skippedRound) ? "NGUỜI THỨ NHẤT" : "NGƯỜI THỨ HAI"} TRÚNG ĐẤU GIÁ`} */}
                                                {`ĐANG XÁC NHẬN ${product.bidderAcceptBid?.user?.userId && product.timeRefusebargainFirt ? "NGƯỜI THỨ HAI" : "NGƯỜI THỨ NHẤT"} TRÚNG ĐẤU GIÁ`}
                                            </span>
                                        </div>
                                        <div className="item-card-action-list">
                                            <Space size={5}>
                                                <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.day}</div>
                                                <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.hour}</div>
                                                <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.minute}</div>
                                                <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.second}</div>
                                            </Space>
                                        </div>
                                    </div>
                                </> : <></>}
                        </>}

                    {/* 
                    - publicProgressAuction: Công khai diễn biến cuộc đấu giá
                    - registerStatus: Trạng thái đăng ký
                    - preCostStatus: Trạng thái nộp tiền đặt trước
                    - isActive: Trạng thái truất quyền đấu giá: 0: hoạt động, 1 là đã truất quyền
                     */}
                    {(product?.statute.publicProgressAuction
                        || product.seller.userId === userState.user.userId
                        || (product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                            product.bidderResponseDTO?.preCostStatus === preCostTypes.DONE)) ?
                        <>
                            {/*  Trả giá xuống */}
                            {product.statute?.auctionMethod?.code === AuctionMethodCode.DECREASE &&
                                // ((socketMessage?.userId && socketMessage?.type === WebsocketTypes.TRA_GIA) ||
                                (product.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE || product.bidderAcceptBid?.status === BidderAcceptBidStatus.ACCEPT) ?
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 15 }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Withdraw style={{ fontSize: '18px' }} />
                                        &nbsp;<span style={{ color: 'white', marginLeft: 5 }}>
                                            {product.statute.allowWithdrawPaid && product.bidderAcceptBid.status === BidderAcceptBidStatus.DONE ?
                                                "THỜI GIAN RÚT LẠI GIÁ" : "THỜI GIAN XÁC NHẬN TRÚNG"}</span>
                                    </div>
                                    <div className="item-card-action-list">
                                        <Space size={5}>
                                            <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.day}</div>
                                            <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.hour}</div>
                                            <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.minute}</div>
                                            <div className="item-card-time" style={{ color: 'white', background: 'transparent' }}>{acceptCountdown.second}</div>
                                        </Space>
                                    </div>
                                </div> : <></>}

                            <Space style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 20 }}>
                                {/* Trả giá lên xác định vòng */}
                                {product?.statute?.auctionType?.code === AuctionTypeCode.HAVE_ROUND &&
                                    product?.statute?.auctionMethod?.code === AuctionMethodCode.INCREASE ?
                                    <div style={{
                                        background: '#97BC2D',
                                        border: '2px solid #FFFFFF',
                                        borderRadius: 10,
                                        textAlign: 'center',
                                        color: 'white',
                                        padding: '10px 15px'
                                    }}>
                                        <div>Vòng</div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                                            {auctionState.level}
                                        </div>
                                    </div> : <></>}

                                <div style={{
                                    background: '#97BC2D',
                                    border: '2px solid #FFFFFF',
                                    borderRadius: 10,
                                    textAlign: 'center',
                                    color: 'white',
                                    padding: '10px 15px'
                                }}>
                                    <div>Giá hiện tại:</div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Statistic
                                            value={startingPrice}
                                            groupSeparator="."
                                            decimalSeparator="."
                                            precision={0}
                                            valueStyle={{ fontFamily: 'Quicksand', color: 'white' }} />
                                        <span style={{ fontSize: 20, marginLeft: 5 }}>VNĐ</span>
                                    </div>
                                </div>

                                {/* Trả giá lên */}
                                {/* 
                                - !upAuctionFinish: Chưa hết giờ trả giá 
                                - registerStatus: Trạng thái đăng kýt
                                - preCostStatus: Trạng thái nộp tiền
                                - startStatus: Trạng thái bắt đầu
                                - skippedRound: Bỏ vòng trước
                                - isActive: Trạng thái truất quyền
                                */}
                                {product?.statute?.auctionMethod?.code === AuctionMethodCode.INCREASE &&
                                    !auctionState.upAuctionFinish &&
                                    product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                                    product.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                                    product?.startStatus === StartStatus.ONGOING &&
                                    !auctionState.skippedRound &&
                                    !product?.bidderResponseDTO?.isActive ?
                                    <div style={{
                                        display: 'flex',
                                        padding: '5px',
                                        background: 'white',
                                        borderRadius: 10,
                                        height: 65
                                    }}>
                                        <InputNumber
                                            min={value}
                                            onWheel={onInputScroll}
                                            onBlur={(event) => onInputBlur(event)}
                                            onFocus={() => onInputFocus()}
                                            value={value}
                                            onChange={onInputChange}
                                            controls={false}
                                            style={{ border: "none", fontSize: 20, display: 'flex', alignItems: 'center', width: '100%' }}
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                            parser={value => (value?.replace(/\$\s?|(\.*)/g, '') || "0")} />
                                        <Button
                                            disabled={buttonDisabled()}
                                            onClick={toggleBidVisible}
                                            className={buttonDisabled() ? "button-gray" : "button-light-yellow"}
                                            style={{ height: '100%', marginLeft: 5 }}>Trả giá</Button>
                                        {product?.statute.allowWithdrawPaid ?
                                            <Button
                                                onClick={toggleWithdrawVisible}
                                                className={"button-red"}
                                                style={{ height: '100%', marginLeft: 5 }}>Rút lại giá</Button> : <></>}
                                    </div> : <></>}

                                {/* 
                                Trường hợp đặt giá xuống 

                                */}
                                {product?.statute?.auctionMethod?.code === AuctionMethodCode.DECREASE &&
                                    product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                                    product.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                                    !product?.bidderResponseDTO?.isActive ?
                                    <>
                                        {/* Khi chưa có người chấp nhận giá hoặc đã có người rút lại giá */}
                                        {!product?.bidderAcceptBid ?
                                            <Button
                                                disabled={auctionState.downPaymentUserAccepted}
                                                className={"button-light-yellow"}
                                                onClick={bidDownAcceptPriceClick}
                                                style={{ marginLeft: 5, height: 40 }}>Chấp nhận giá</Button> : <></>}

                                        {/* Khi người dùng hiện tại chấp nhận giá và được phép rút lại giá*/}
                                        {product?.statute.allowWithdrawPaid && product?.bidderAcceptBid && product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE
                                            && (product?.bidderAcceptBid?.user?.userId && product.bidderAcceptBid.user.userId === userState.user.userId) ?
                                            <>
                                                <Button
                                                    onClick={() => onBidConfirmClick()}
                                                    className={"button-light-yellow"}
                                                    style={{ marginLeft: 5, height: 40 }}>Xác nhận trúng</Button>

                                                {/* {product?.statute.allowWithdrawPaid ? */}
                                                <Button
                                                    disabled={auctionState.downPaymentUserAccepted}
                                                    onClick={toggleWithdrawVisible}
                                                    className={"button-red"}
                                                    style={{ marginLeft: 5, height: 40 }}>Rút lại giá</Button>
                                                {/* : <></>} */}
                                            </> : <></>}
                                    </> : <></>}


                            </Space>
                        </> : <></>}
                </> : <></>
            }

            {/* Hiển thị khi tạm dừng đấu giá */}
            {product?.startStatus === StartStatus.PAUSE &&
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Gavel style={{ fontSize: '18px' }} />
                    &nbsp;<span style={{ color: 'white', marginLeft: 5 }}> ĐÃ TẠM DỪNG ĐẤU GIÁ</span>
                </div>
            }

            {/* Hiển thị khi đấu giá không thành công */}
            {product?.startStatus === StartStatus.FINISH && (!product?.bidderAcceptBid || product?.bidderAcceptBid.status !== BidderAcceptBidStatus.SUCCESS) ?
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Failed style={{ fontSize: '18px' }} />
                    &nbsp;<span style={{ color: 'white', marginLeft: 5 }}> ĐẤU GIÁ KHÔNG THÀNH CÔNG</span>
                </div> : <></>}

            {/* Hiển thị khi đấu giá thành công */}
            {product?.startStatus === StartStatus.FINISH && product?.bidderAcceptBid && product?.bidderAcceptBid?.status === BidderAcceptBidStatus.SUCCESS ?
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {product?.bidderAcceptBid.user?.userId === userState.user.userId ?
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Cup style={{ fontSize: '18px' }} />
                            <span style={{ color: 'white', marginLeft: 5, fontSize: 16 }}> BẠN ĐÃ TRÚNG ĐẤU GIÁ</span>
                        </div> :
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Payment style={{ fontSize: '18px' }} />
                            <span style={{ color: 'white', marginLeft: 5, fontSize: 16 }}> GIÁ TRÚNG ĐẤU GIÁ</span>
                        </div>
                    }
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Award style={{ fontSize: 18 }} />
                        <div style={{ color: 'white', marginLeft: 5, display: 'flex', alignItems: 'center' }}>
                            <Statistic
                                value={product.bidderAcceptBid.valueOfBid}
                                groupSeparator="."
                                decimalSeparator="."
                                precision={0}
                                valueStyle={{ fontFamily: 'Quicksand', color: 'white', marginRight: 5, fontSize: 18 }} /> VNĐ
                        </div>
                    </div>

                </div> : <></>}


            {bidVisible && product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                !product?.bidderResponseDTO?.isActive &&
                userState.user.userId ?
                <ConfirmModal
                    isLoading={isLoading}
                    onCancel={toggleBidVisible}
                    onOk={onPriceSubmitClick}
                    status="error"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="font-size-16">Bạn có chắc chắn muốn trả giá</div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Statistic
                                value={value}
                                groupSeparator="."
                                decimalSeparator="."
                                precision={0}
                                valueStyle={{ fontFamily: 'Quicksand', fontSize: 16, marginLeft: 5 }} />
                            <span style={{ marginLeft: 5, fontSize: 16 }}>VNĐ</span>
                        </div>
                    </div>}
                    visible={bidVisible}
                />
                : <></>}

            {withdrawVisible && product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                !product?.bidderResponseDTO?.isActive &&
                userState.user.userId ?
                <ConfirmModal
                    onCancel={toggleWithdrawVisible}
                    onOk={onWidthdrawClick}
                    status="error"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div className="font-size-16">Bạn có chắc chắn muốn rút lại giá đã chấp nhận trả?</div>
                        <div className="font-size-16">Chú ý: Nếu nhấn Xác nhận, bạn sẽ không được hoàn lại tiền đặt trước</div>
                    </div>}
                    visible={withdrawVisible}
                    okText="XÁC NHẬN"
                    cancelText="THOÁT"
                />
                : <></>}

            {auctionState.acceptOpen &&
                product?.startStatus &&
                product.startStatus !== StartStatus.FINISH &&
                product.startStatus !== StartStatus.PAUSE &&
                product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                product?.bidderResponseDTO?.isActive !== 1 &&
                (product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE || product?.bidderAcceptBid?.status === BidderAcceptBidStatus.ACCEPT) &&
                !auctionState.declinedWin &&
                userState.user.userId ?
                <AuctionAcceptConfirmModal
                    onDeny={toggleRefuseVisible}
                    onCancel={() => { }}
                    onOk={onAcceptClick}
                    status="success"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div className="font-size-16">
                            Chúc mừng bạn đã trúng đấu giá tài sản {product?.productName} với giá
                            <Statistic
                                style={{ display: 'inline-flex' }}
                                value={Number(value) || product?.bidderAcceptBid?.valueOfBid}
                                groupSeparator="."
                                decimalSeparator="."
                                precision={0}
                                valueStyle={{ fontFamily: 'Quicksand', fontSize: 16, marginLeft: 5 }}
                                suffix="VNĐ" />
                            {product?.timeRefusebargainFirt ? " do người trúng đấu giá đã từ chối." : "."}
                        </div>
                        <div className="font-size-16" style={{ whiteSpace: 'nowrap' }}>Để xác nhận trúng đấu giá, bạn vui lòng bấm Xác nhận.</div>
                        <div className="font-size-16" style={{ whiteSpace: 'nowrap' }}>
                            {/* {acceptCountdown.day || 0} ngày {acceptCountdown.hour || 0} giờ {acceptCountdown.minute || 0} phút {acceptCountdown.second || 0} giây */}
                            {generateCountdownText()}
                        </div>
                    </div>}
                    visible={auctionState.acceptOpen}
                    okText="XÁC NHẬN"
                    cancelText="TỪ CHỐI"
                />
                : <></>}

            {refuseVisible &&
                product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                userState.user.userId ?
                <ConfirmModal
                    onCancel={toggleRefuseVisible}
                    onOk={onDenyClick}
                    status="error"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div className="font-size-16">Bạn đang thực hiện từ chối trúng đấu giá, bạn có chắc chắn từ chối hay không?</div>
                        <div className="font-size-16">Chú ý: Nếu nhấn Xác nhận, bạn sẽ không được hoàn lại tiền đặt trước</div>
                    </div>}
                    visible={refuseVisible}
                    okText="XÁC NHẬN"
                    cancelText="THOÁT"
                />
                : <></>}

            {successVisible && product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                // product?.bidderResponseDTO?.isActive !== 1 &&
                userState.user.userId ?
                <ConfirmModal
                    onCancel={toggleSuccessVisible}
                    onOk={onSuccessClick}
                    status="success"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div className="font-size-16">Bạn đã xác nhận trúng đấu giá tài sản thành công.</div>
                        <div className="font-size-16">Bạn vui lòng liên hệ Trung tâm để hoàn tất thủ tục mua tài sản.</div>
                        <div className="font-size-16">Cảm ơn và hẹn gặp lại bạn trong cuộc đấu giá khác!</div>
                    </div>}
                    visible={successVisible}
                    okText="ĐỒNG Ý"
                    noCancelButton={true}
                />
                : <></>}

            {refuseSuccessVisible && product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                // product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                // product?.bidderResponseDTO?.isActive !== 1 &&
                userState.user.userId ?
                <ConfirmModal
                    onCancel={toggleRefuseSuccessVisible}
                    onOk={toggleRefuseSuccessVisible}
                    status="success"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div className="font-size-16">Bạn đã từ chối trúng đấu giá tài sản thành công.</div>
                        <div className="font-size-16">Cảm ơn và hẹn gặp lại bạn trong cuộc đấu giá khác!</div>
                    </div>}
                    visible={refuseSuccessVisible}
                    okText="ĐỒNG Ý"
                    noCancelButton={true}
                />
                : <></>}

            {waitForTurnVisible && product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                product?.bidderResponseDTO?.isActive !== 1 &&
                product?.bidderAcceptBid &&
                product?.bidderAcceptBid?.status === BidderAcceptBidStatus.DONE &&
                product?.startStatus &&
                product.startStatus === StartStatus.ONGOING &&
                userState.user.userId ?
                <ConfirmModal
                    onCancel={() => setWaitForTurnVisible(false)}
                    onOk={() => setWaitForTurnVisible(false)}
                    status="error"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div className="font-size-16">Đã có người chấp nhận mức giá hiện tại.</div>
                        <div className="font-size-16">Thời gian còn lại để người này xác nhận trúng đấu giá:</div>
                        <div className="font-size-16" style={{ margin: '30px 0px' }}>
                            {/* {Object.values(acceptCountdown).map(item => {
                                if (item < 10) {
                                    return "0" + item.toString();
                                }
                                return item.toString();
                            }).join(":")} */}
                            {generateCountdownText()}
                        </div>
                        <div className="font-size-14">Chú ý: Nếu người đầu tiên chấp nhận giá rút lại giá đã trả cuộc đấu giá vẫn tiếp tục diễn ra từ mức giá hiện tại.
                            Thời gian đấu giá của mức giá hiện tại được tạm dừng.</div>
                    </div>}
                    visible={waitForTurnVisible}
                    okText="ĐỒNG Ý"
                    noCancelButton={true}
                />
                : <></>}

            {secondUserVisible &&
                product?.bidderResponseDTO?.registerStatus === registerTypes.SUCCESS &&
                product?.bidderResponseDTO?.preCostStatus === preCostTypes.DONE &&
                product.startStatus && product?.startStatus === StartStatus.ONGOING &&
                userState.user.userId ?
                <ConfirmModal
                    onCancel={toggleSecondUserVisible}
                    onOk={toggleSecondUserVisible}
                    status="error"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div className="font-size-16">Bạn có cơ hội trúng đấu giá nếu người trả giá cao nhất từ chối.</div>
                        <div className="font-size-16">Bạn vui lòng chờ người trả giá cao nhất xác nhận trong</div>
                        <div className="font-size-16" style={{ whiteSpace: 'nowrap' }}>
                            {/* {acceptCountdown.day || 0} ngày {acceptCountdown.hour || 0} giờ {acceptCountdown.minute || 0} phút {acceptCountdown.second || 0} giây */}
                            {generateCountdownText()}
                        </div>
                    </div>}
                    visible={secondUserVisible}
                    okText="ĐỒNG Ý"
                    noCancelButton={true}
                />
                : <></>}

        </div>
    )
}

export default AuctionPriceStatus;